import React, { ReactElement, lazy } from 'react';

import { Navigate } from 'react-router';

import { PROD_REGISTRATION_BRANDS, PROD_REGISTRATION_COUNTRIES } from '@/config/common';
import { ROLES, SP_ROLES } from '@/config/user';

import { isNewHistoryFeaturesEnabled, isProductManualsEnabled } from '@/constants';

import { SingleSystemSettingsTab } from '@/pages/SystemSettings/enum';
import { singleSystemSettingsUrls } from '@/pages/SystemSettings/urls';
import { RootState } from '@/store/store';
import { Brands } from '@jpi-cloud-web-pro/jpi-cloud-shared/config/brands';
import AuthGuard from './guards/AuthGuard';
import UnAuthGuard from './guards/UnAuthGuard';
import { AppRouteObject } from './types';
import { getSystemMenuCrumbs, getSystemStatusCrumbs } from './utils';

export const EmptyLayout = lazy(() => import(/* webpackChunkName: "EmptyLayout" */ '@/components/layout/EmptyLayout'));
export const SignedInLayout = lazy(
  () => import(/* webpackChunkName: "SignedInLayout" */ '@/components/layout/SignInLayout'),
);
export const SignedOutLayout = lazy(
  () => import(/* webpackChunkName: "SignedOutLayout" */ '@/components/layout/SignOutLayout'),
);

const Dashboard = lazy(() => import(/* webpackChunkName: "Dashboard" */ '@/pages/Dashboard'));
const AddDevice = lazy(() => import(/* webpackChunkName: "AddDevice" */ '@/pages/AddDevice'));
const ProductRegistration = lazy(
  () => import(/* webpackChunkName: "ProductRegistration" */ '@/pages/ProductRegistration'),
);

const NoSubscription = lazy(() => import(/* webpackChunkName: "NoSubscription" */ '@/pages/NoSubscription'));
const SystemGroupsList = lazy(() => import(/* webpackChunkName: "SystemGroupsList" */ '@/pages/SystemGroupsList'));

const NewPassword = lazy(() => import(/* webpackChunkName: "NewPassword" */ '@/pages/NewPassword'));
const ForgotPassword = lazy(() => import(/* webpackChunkName: "ForgotPassword" */ '@/pages/ForgotPassword'));
const Register = lazy(() => import(/* webpackChunkName: "Register" */ '@/pages/Register'));

const SelectOrganization = lazy(() => import(/* webpackChunkName: "Login" */ '@/pages/Login/pages/SelectOrganization'));
const Login = lazy(() => import(/* webpackChunkName: "Login" */ '@/pages/Login/pages/Login'));

// import Home from "./Home";
const FaqPage = lazy(() => import(/* webpackChunkName: "Faq" */ '@/pages/Faq'));
const FaqLegalPage = lazy(() => import(/* webpackChunkName: "Faq" */ '@/pages/Faq/FaqLegal'));
const CookiesPage = lazy(() => import(/* webpackChunkName: "Cookies" */ '@/pages/Cookies'));
const UpdateDeviceFirmware = lazy(
  () => import(/* webpackChunkName: "UpdateDeviceFirmware" */ '@/pages/UpdateDeviceFirmware'),
);
const FirmwareReleaseNotes = lazy(
  () => import(/* webpackChunkName: "UpdateDeviceFirmware" */ '@/pages/UpdateDeviceFirmware/FirmwareReleaseNotes'),
);
const ContactPage = lazy(() => import(/* webpackChunkName: "Contact" */ '@/pages/Contact'));
const SmartGuidePage = lazy(() => import(/* webpackChunkName: "SmartGuide" */ '@/pages/SmartGuide'));
const ProfilePage = lazy(() => import(/* webpackChunkName: "ProfilePage" */ '@/pages/ProfilePage'));
const Reports = lazy(() => import(/* webpackChunkName: "Reports" */ '@/pages/Reports'));
const ReportInfo = lazy(() => import(/* webpackChunkName: "Reports" */ '@/pages/Reports/components/ReportInfo'));
const SystemSettings = lazy(() => import(/* webpackChunkName: "SystemSettings" */ '@/pages/SystemSettings'));
const SystemStatusTab = lazy(
  () => import(/* webpackChunkName: "SystemStatusTab" */ '@/pages/SystemSettings/components/SystemStatusTab'),
);
const SystemHistoryTab = lazy(
  () => import(/* webpackChunkName: "SystemHistoryTab" */ '@/pages/SystemSettings/components/SystemHistoryTab'),
);
const SystemHistoryTabOld = lazy(
  () =>
    import(/* webpackChunkName: "SystemSettingsHistoryOld" */ '@/pages/SystemSettings/components/SystemHistoryTabOld'),
);
const SystemEventLogTab = lazy(
  () => import(/* webpackChunkName: "SystemEventLogTab" */ '@/pages/SystemSettings/components/SystemEventLogTab'),
);
const SystemNotificationsTab = lazy(
  () =>
    import(/* webpackChunkName: "SystemNotificationsTab" */ '@/pages/SystemSettings/components/SystemNotificationsTab'),
);
const SystemDiagramTab = lazy(
  () => import(/* webpackChunkName: "SystemDiagramTab" */ '@/pages/SystemSettings/components/SystemDiagramTab'),
);
const SystemDevicesTab = lazy(
  () => import(/* webpackChunkName: "SystemDevicesTab" */ '@/pages/SystemSettings/components/SystemDevicesTab'),
);
const SystemCustomerInfoTab = lazy(
  () =>
    import(/* webpackChunkName: "SystemCustomerInfoTab" */ '@/pages/SystemSettings/components/SystemCustomerInfoTab'),
);
const SystemAboutManufacturerTab = lazy(
  () =>
    import(
      /* webpackChunkName: "SystemAboutManufacturerTab" */ '@/pages/SystemSettings/components/AboutManufacturerTab'
    ),
);
const SystemSchedulingTab = lazy(
  () => import(/* webpackChunkName: "SystemSchedulingTab" */ '@/pages/SystemSettings/components/SchedulingTab'),
);
const SystemDocumentsTab = lazy(
  () => import(/* webpackChunkName: "SystemDocumentsTab" */ '@/pages/SystemSettings/components/SystemDocuments'),
);
const SystemMenuTab = lazy(
  () => import(/* webpackChunkName: "SystemMenuTab" */ '@/pages/SystemSettings/components/SystemMenuTab'),
);

const Invitations = lazy(() => import(/* webpackChunkName: "Invitations" */ '@/pages/Invitations'));
const ProAccounts = lazy(() => import(/* webpackChunkName: "ProAccounts" */ '@/pages/ProAccounts'));
const AddInvitation = lazy(() => import(/* webpackChunkName: "AddInvitation" */ '@/pages/AddInvitation'));
const NewServicePartner = lazy(() => import(/* webpackChunkName: "NewServicePartner" */ '@/pages/NewServicePartner'));
const EditServicePartner = lazy(
  () => import(/* webpackChunkName: "EditServicePartner" */ '@/pages/EditServicePartner'),
);
const SendConfirmEmailLink = lazy(
  () => import(/* webpackChunkName: "SendConfirmEmailLink" */ '@/pages/SendConfirmEmailLink'),
);
const DeleteConfirmation = lazy(
  () => import(/* webpackChunkName: "DeleteConfirmation" */ '@/pages/DeleteConfirmation'),
);
const LeaveConfirmation = lazy(() => import(/* webpackChunkName: "LeaveConfirmation" */ '@/pages/LeaveConfirmation'));
const DeleteProAccountConfirmation = lazy(
  () => import(/* webpackChunkName: "DeleteProAccountConfirmation" */ '@/pages/DeleteProAccountConfirmation'),
);
const ConfirmEmail = lazy(() => import(/* webpackChunkName: "ConfirmEmail" */ '@/pages/ConfirmEmail'));
// const OrderCancel = lazy(() => import(/* webpackChunkName: "OrderCancel" */ '@/pages/OrderCancel'));
const ConfirmEmailUpdate = lazy(
  () => import(/* webpackChunkName: "ConfirmEmailUpdate" */ '@/pages/ConfirmEmailUpdate'),
);

const PrivacyPolicyLegalPage = lazy(
  () => import(/* webpackChunkName: "PrivacyPolicyLegalPage" */ '@/pages/LegalPages/PrivacyPolicyLegalPage'),
);
const TermsOfServiceLegalPage = lazy(
  () => import(/* webpackChunkName: "TermsOfServiceLegalPage" */ '@/pages/LegalPages/TermsOfServiceLegalPage'),
);

const ProTermsOfServiceLegalPage = lazy(
  () => import(/* webpackChunkName: "ProTermsOfServiceLegalPage" */ '@/pages/LegalPages/ProTermsOfServiceLegalPage'),
);

const ProTermsOfServicePage = lazy(
  () => import(/* webpackChunkName: "ProTermsOfService" */ '@/pages/ProTermsOfService'),
);
const TermsOfServicePage = lazy(() => import(/* webpackChunkName: "TermsOfService" */ '@/pages/TermsOfService'));
const PrivacyPolicyPage = lazy(() => import(/* webpackChunkName: "PrivacyPolicy" */ '@/pages/PrivacyPolicy'));

const WorksWith = lazy(() => import(/* webpackChunkName: "WorksWith" */ '@/pages/WorksWith'));
const Integration = lazy(() => import(/* webpackChunkName: "Integration" */ '@/pages/Integration'));
const AboutPage = lazy(() => import(/* webpackChunkName: "About" */ '@/pages/About'));
const Store = lazy(() => import(/* webpackChunkName: "Store" */ '@/pages/Store'));
const Checkout = lazy(() => import(/* webpackChunkName: "Checkout" */ '@/pages/Checkout'));

const RedeemVoucher = lazy(() => import(/* webpackChunkName: "RedeemVoucher" */ '@/pages/RedeemVoucher'));

export const AnonymousGuard = (props): ReactElement => <>{props.children}</>;
export const DefaultLayout = (props): ReactElement => <SignedOutLayout isNotAuthPage {...props} />;

const ALL_ROLES = Object.values(ROLES);
const ALL_SP_ROLES = Object.values(SP_ROLES);

const isHiddenForBrand =
  (comparedBrandId: string) =>
  ({ brandId }) =>
    (brandId ?? '').toLowerCase() === comparedBrandId.toLowerCase();

const routes = {
  anonymousRoutes: [
    {
      path: '/login',
      page: Login,
      layout: SignedOutLayout,
      guard: UnAuthGuard,
    },
    {
      path: '/sign-up',
      page: Login,
      layout: SignedOutLayout,
      guard: UnAuthGuard,
    },
    {
      path: '/forgot-password',
      layout: SignedOutLayout,
      page: ForgotPassword,
      guard: UnAuthGuard,
    },
    {
      path: '/password-reset',
      page: NewPassword,
      layout: SignedOutLayout, // TODO: Check this layout
      guard: UnAuthGuard,
    },
    {
      path: '/register',
      layout: SignedOutLayout,
      page: Register,
      guard: UnAuthGuard,
    },
    {
      path: '/confirmation-link',
      layout: SignedOutLayout,
      page: SendConfirmEmailLink,
      guard: UnAuthGuard,
    },
    {
      path: '/delete/confirmation',
      layout: SignedOutLayout,
      page: DeleteConfirmation,
      guard: AnonymousGuard,
    },
    {
      path: '/pro-account/leave/confirmation',
      layout: SignedOutLayout,
      page: LeaveConfirmation,
      guard: AnonymousGuard,
    },
    {
      path: '/pro-account/delete/confirmation',
      layout: SignedOutLayout,
      page: DeleteProAccountConfirmation,
      guard: AnonymousGuard,
    },
    {
      path: '/confirm-email/:token',
      layout: SignedOutLayout,
      page: ConfirmEmail,
      guard: UnAuthGuard,
    },
    {
      path: '/confirm-email-update',
      page: ConfirmEmailUpdate,
      layout: SignedOutLayout,
      guard: AnonymousGuard,
    },
    {
      path: '/legal/privacy-policy/:locale?',
      layout: DefaultLayout,
      page: PrivacyPolicyLegalPage,
      // Anonymous, not UnAuthGuard: these public legal URLs are linked from outside the app,
      // so signed-in users must get the content instead of a redirect to /systems.
      guard: AnonymousGuard,
    },
    {
      path: '/legal/privacy-policy-step-1/:locale?',
      layout: EmptyLayout,
      page: PrivacyPolicyLegalPage,
      guard: AnonymousGuard,
    },
    {
      path: '/legal/terms-of-service/:locale?',
      layout: DefaultLayout,
      page: TermsOfServiceLegalPage,
      guard: AnonymousGuard,
    },
    {
      path: '/legal/terms-of-service-step-3/:locale?',
      layout: EmptyLayout,
      page: ProTermsOfServiceLegalPage,
      guard: AnonymousGuard,
    },
    {
      path: '/legal/terms-of-service-step-1/:locale?',
      layout: EmptyLayout,
      page: TermsOfServiceLegalPage,
      guard: AnonymousGuard,
    },
    {
      path: '/legal/works-with',
      layout: DefaultLayout,
      page: WorksWith,
      guard: UnAuthGuard,
    },
    {
      path: '/legal/about',
      layout: DefaultLayout,
      page: AboutPage,
      guard: UnAuthGuard,
    },
    {
      path: '/legal/faq/:locale?',
      page: FaqLegalPage,
      layout: DefaultLayout,
      guard: AnonymousGuard,
    },
  ],

  restrictedGuestRoutes: [
    {
      path: '/login/select-organization',
      page: SelectOrganization,
      layout: SignedOutLayout,
      requiredRoles: [ROLES.guest],
      guard: AuthGuard, // AnonymousGuard
    },
    {
      path: '/new-service-partner',
      page: NewServicePartner,
      layout: SignedInLayout,
      requiredRoles: [ROLES.guest],
      guard: AuthGuard,
      heroConfig: { noHeading: true, noHero: false },
    },
    {
      path: '/profile-settings/:tab?',
      page: ProfilePage,
      layout: SignedInLayout,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/faq',
      page: FaqPage,
      layout: SignedInLayout,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHeading: true, noHero: false },
    },
    {
      path: '/about',
      page: AboutPage,
      layout: SignedInLayout,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false, noHeading: true },
    },
    {
      path: '/cookies',
      page: CookiesPage,
      layout: SignedInLayout,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/contact',
      page: ContactPage,
      layout: SignedInLayout,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/pro-terms-of-service-step-:step',
      page: ProTermsOfServicePage,
      layout: SignedInLayout,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/pro-terms-of-service',
      // eslint-disable-next-line react/display-name
      page: () => <Navigate to="/pro-terms-of-service-step-1" replace />,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/terms-of-service',
      page: TermsOfServicePage,
      layout: SignedInLayout,
      guard: AuthGuard,
      requiredRoles: [ROLES.admin],
    },
    {
      path: '/privacy-policy',
      page: PrivacyPolicyPage,
      layout: SignedInLayout,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
    },
    {
      path: '/works-with',
      page: WorksWith,
      layout: SignedInLayout,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false, noHeading: true },
    },
    {
      path: '/nibeflow-integration',
      page: Integration,
      layout: SignedInLayout,
      requiredRoles: [ROLES.admin],
      guard: AuthGuard,
      heroConfig: { noHero: false, noHeading: true },
    },
    {
      path: '/cancellation',
      // eslint-disable-next-line react/display-name
      page: () => <Navigate to="/store/subscriptions" replace />,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/pro-accounts',
      layout: SignedInLayout,
      page: ProAccounts,
      requiredRoles: ALL_SP_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
  ],

  restrictedRoutes: [
    {
      path: '/update-firmware',
      layout: SignedInLayout,
      page: UpdateDeviceFirmware,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHeading: true, noHero: false },
    },
    {
      path: '/update-firmware/release-notes/:firmwareTypeId/:brandId',
      layout: SignedInLayout,
      page: FirmwareReleaseNotes,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/reports/:type/:reportDate',
      layout: SignedInLayout,
      page: ReportInfo,
      guard: AuthGuard,
      requiredRoles: ALL_SP_ROLES,
      subscribedUser: true,
      heroConfig: { noHero: false },
    },
    {
      path: '/reports',
      layout: SignedInLayout,
      page: Reports,
      guard: AuthGuard,
      requiredRoles: ALL_SP_ROLES,
      subscribedUser: true,
      heroConfig: { noHero: false },
    },
    {
      path: '/smartguide',
      layout: SignedInLayout,
      page: SmartGuidePage,
      requiredRoles: ALL_SP_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/service-partner-settings/:tab?',
      layout: SignedInLayout,
      page: EditServicePartner,
      requiredRoles: ALL_SP_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
    {
      path: '/redeem-voucher',
      layout: SignedInLayout,
      page: RedeemVoucher,
      guard: AuthGuard,
      requiredRoles: [ROLES.admin, ROLES.buyer],
      // TODO: Setup brand page selection
      brands: [Brands.NIBE, Brands.CLIMATEMASTER],
      isHidden: isHiddenForBrand(Brands.CLIMATEMASTER),
      heroConfig: { noHero: false },
    },
    {
      path: '/no-subscription',
      layout: SignedInLayout,
      page: NoSubscription,
      requiredRoles: ALL_ROLES,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
  ],

  premiumRoutes: [
    {
      path: '/add-device',
      layout: SignedInLayout,
      page: AddDevice,
      guard: AuthGuard,
      requiredRoles: [SP_ROLES.admin, SP_ROLES.manager],
      subscribedUser: true, // NOTE we can also use -> type: ROUTE_TYPES.Premium
      isHidden: process.env.FEATURE_ADD_DEVICE_ENABLED !== 'true', // remove this line prop when FF FEATURE_ADD_DEVICE_ENABLED should be removed
    },
    {
      path: '/systems',
      layout: SignedInLayout,
      page: Dashboard,
      guard: AuthGuard,
      requiredRoles: ALL_SP_ROLES,
      subscribedUser: true, // NOTE we can also use -> type: ROUTE_TYPES.Premium
    },
    {
      path: singleSystemSettingsUrls.index,
      layout: SignedInLayout,
      page: SystemSettings,
      guard: AuthGuard,

      requiredRoles: ALL_SP_ROLES,
      subscribedUser: true,
      heroConfig: { isBrandedPage: true, noHero: false },
      isHidden: params => {
        return !isProductManualsEnabled && params.tab === SingleSystemSettingsTab.Documents;
      },
      children: [
        {
          index: true,
          page: () => <Navigate to={SingleSystemSettingsTab.Status} replace />,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          subscribedUser: true,
        },
        {
          path: SingleSystemSettingsTab.Status,
          page: SystemStatusTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
          handle: {
            selector: (state: RootState) => {
              const selectedSystem = state.app.selectedSystem;
              const ancestors = !state.systemSettings.loading && (state.systemSettings.ancestors || []);
              return {
                selectedSystem,
                ancestors,
              };
            },
            crumb: getSystemStatusCrumbs,
          },
        },
        {
          path: SingleSystemSettingsTab.History,
          page: isNewHistoryFeaturesEnabled ? SystemHistoryTab : SystemHistoryTabOld,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
        {
          path: SingleSystemSettingsTab.EventLog,
          page: SystemEventLogTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
        {
          path: SingleSystemSettingsTab.Notifications,
          page: SystemNotificationsTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
        {
          path: SingleSystemSettingsTab.Menu,
          page: SystemMenuTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
          handle: {
            crumb: ({ pathname, intl }) => ({
              title: intl.formatMessage({ id: 'label.main-menu', defaultMessage: 'Main menu' }),
              path: pathname,
            }),
          },
        },
        {
          path: `${SingleSystemSettingsTab.Menu}/${singleSystemSettingsUrls.menuId}`,
          page: SystemMenuTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
          handle: {
            crumb: getSystemMenuCrumbs,
          },
        },
        {
          path: SingleSystemSettingsTab.AboutManufacturer,
          page: SystemAboutManufacturerTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
        {
          path: SingleSystemSettingsTab.DeviceScheduling,
          page: SystemSchedulingTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
        {
          path: SingleSystemSettingsTab.Documents,
          page: SystemDocumentsTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
        {
          path: SingleSystemSettingsTab.SystemDiagram,
          page: SystemDiagramTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
        {
          path: SingleSystemSettingsTab.Devices,
          page: SystemDevicesTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
        {
          path: `${SingleSystemSettingsTab.CustomerInfo}`,
          page: SystemCustomerInfoTab,
          guard: AuthGuard,
          requiredRoles: ALL_SP_ROLES,
          heroConfig: { isBrandedPage: true, noHero: false },
        },
      ],
    },

    // TODO: Refactor and use feature based routing ** START **
    {
      path: '/system-groups/:topLevelGroupId/:groupLevel2Id/:groupLevel3Id/:groupLevel4Id',
      layout: SignedInLayout,
      page: SystemGroupsList,
      guard: AuthGuard,
      requiredRoles: ALL_SP_ROLES,
      subscribedUser: true,
    },
    {
      path: '/system-groups/:topLevelGroupId/:groupLevel2Id/:groupLevel3Id',
      layout: SignedInLayout,
      page: SystemGroupsList,
      guard: AuthGuard,
      requiredRoles: ALL_SP_ROLES,
      subscribedUser: true,
    },
    {
      path: '/system-groups/:topLevelGroupId/:groupLevel2Id',
      layout: SignedInLayout,
      page: SystemGroupsList,
      guard: AuthGuard,
      requiredRoles: ALL_SP_ROLES,
      subscribedUser: true,
    },
    {
      path: '/system-groups/:topLevelGroupId',
      layout: SignedInLayout,
      page: SystemGroupsList,
      guard: AuthGuard,
      requiredRoles: ALL_SP_ROLES,
      subscribedUser: true,
    },
    // TODO: Refactor and use feature based routing ** END **

    {
      path: '/product-registration/*', // Changed to /* for nested routes in React Router v6
      layout: SignedInLayout,
      page: ProductRegistration,
      eligibleCountries: PROD_REGISTRATION_COUNTRIES,
      eligibleBrands: PROD_REGISTRATION_BRANDS,
      requiredRoles: ALL_SP_ROLES,
      efficiencyPartner: true,
      subscribedUser: true,
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
  ],

  brandedPremiumRoutes: [
    {
      path: '/request-access',
      layout: SignedInLayout,
      page: Invitations,
      guard: AuthGuard,
      requiredRoles: [SP_ROLES.admin, SP_ROLES.manager],
      subscribedUser: true,
      heroConfig: { noHero: false },
    },
    {
      path: '/add-request-access',
      layout: SignedInLayout,
      page: AddInvitation,
      guard: AuthGuard,
      requiredRoles: [SP_ROLES.admin, SP_ROLES.manager],
      subscribedUser: true,
      heroConfig: { noHero: false },
    },
  ],

  store: [
    {
      path: '/store/checkout/:checkoutId', // TODO: Confirm this redirect with business
      // eslint-disable-next-line react/display-name
      page: () => <Navigate to="/store/shopping-cart?checkout=:checkoutId" replace />,
      guard: AuthGuard,
      isHidden: isHiddenForBrand(Brands.CLIMATEMASTER),
      heroConfig: { noHero: false },
    },
    {
      path: '/store/orders/:orderId',
      // eslint-disable-next-line react/display-name
      page: () => <Navigate to="/store/order-history?order=:orderId" replace />,
      guard: AuthGuard,
      isHidden: isHiddenForBrand(Brands.CLIMATEMASTER),
      heroConfig: { noHero: false },
    },
    {
      path: '/store/:tab?',
      layout: SignedInLayout,
      page: Store,
      guard: AuthGuard,
      requiredRoles: [SP_ROLES.admin, SP_ROLES.buyer],
      isHidden: isHiddenForBrand(Brands.CLIMATEMASTER),
      heroConfig: { noHero: false },
    },
  ],
  checkout: [
    {
      path: '/checkout/:checkoutId',
      layout: SignedInLayout,
      page: Checkout,
      requiredRoles: [SP_ROLES.admin, SP_ROLES.buyer, SP_ROLES.manager],
      guard: AuthGuard,
      heroConfig: { noHero: false },
    },
  ],
};

export const allRoutes: AppRouteObject[] = [
  ...routes.anonymousRoutes,
  ...routes.restrictedGuestRoutes,
  ...routes.restrictedRoutes,
  ...routes.premiumRoutes,
  ...routes.brandedPremiumRoutes,
  ...routes.store,
  ...routes.checkout,
];

export const routeTree: AppRouteObject[] = [
  {
    id: 'signed-in-layout',
    layout: SignedInLayout,
    children: allRoutes.filter(({ guard, layout }) => guard === AuthGuard && layout !== SignedOutLayout),
  },
  {
    id: 'signed-out-layout',
    layout: SignedOutLayout,
    children: allRoutes.filter(({ layout }) => layout === SignedOutLayout),
  },
  {
    id: 'default-layout',
    layout: DefaultLayout,
    children: allRoutes.filter(({ layout }) => layout === DefaultLayout),
  },
  {
    id: 'empty-layout',
    layout: EmptyLayout,
    children: allRoutes.filter(({ layout }) => layout === EmptyLayout),
  },
];

export default routes;
